import React, { useMemo } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Box,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Label from "../../../components/Label/Label";

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const MainTable = React.memo(
  ({
    headers = [],
    data = [],
    loading = false,
    error = null,
    emptyMessage = "No data available.",
    title = "",
    plotType = "",
  }) => {
    const headerCells = useMemo(
      () =>
        headers.map((header, idx) => {
          if (header === "Ppk_status") return null;

          return (
            <TableCell
              key={idx}
              sx={{
                fontWeight: "bold",
                backgroundColor: "#E9E9E9",
                padding: "8px 10px",
                position: "sticky",
                top: 0,
                zIndex: 2,
              }}
            >
              <Label
                text={header}
                textTransform="none"
                fontWeight="500"
                fontSize="12px"
              />
            </TableCell>
          );
        }),
      [headers],
    );

    // Memoize rows mapping
    const tableRows = useMemo(
      () =>
        data.map((row, rowIndex) => (
          <StyledTableRow key={rowIndex}>
            {headers.map((header, colIndex) => {
              if (header === "Ppk_status") return null;
              let color = "inherit";
              if (header === "Ppk") {
                color = row["Ppk_status"];
              }

              return (
                <TableCell key={colIndex} sx={{ padding: "8px 10px" }}>
                  <Label
                    text={row[header]}
                    fontWeight="500"
                    fontSize="12px"
                    color={color}
                  />
                </TableCell>
              );
            })}
          </StyledTableRow>
        )),
      [data, headers],
    );

    if (loading) {
      return (
        <Box py={4} textAlign="center">
          <Typography variant="body2">Loading...</Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box py={4} textAlign="center">
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        </Box>
      );
    }

    if (!data || data.length === 0) {
      return (
        <Box py={4} textAlign="center">
          <Label text={emptyMessage} fontWeight="500" />
        </Box>
      );
    }

    return (
      <Box>
        {title && (
          <Box ml={1}>
            <Label
              text={title}
              fontWeight="600"
              fontSize="14px"
              color="#181D27"
              textTransform="none"
            />
          </Box>
        )}

        <TableContainer
          component={Paper}
          sx={{
            mt: 1,
            borderRadius: 2,
            boxShadow: "none",
            border: "1px solid #e0e0e0",
            overflowY: "auto",
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>{headerCells}</TableRow>
            </TableHead>
            <TableBody>{tableRows}</TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  },
);

export default MainTable;
